/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-operational-tools-form',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './operational-tools-form.component.html',
  styleUrl: './operational-tools-form.component.scss',
})
export class OperationalToolsFormComponent implements OnInit {
  @Input() initialValue: any;
  @Output() formSubmit = new EventEmitter<any>();

  operationalToolsForm: FormGroup;

  businessBankingOptions = [
    'Open or manage business accounts (checking, savings, investments)',
    'Help with payroll and HR',
    'Get a line of credit',
    "Help managing your company's cash flow",
    'Advice on financial strategy',
  ];

  marketingHelpOptions = [
    'Help designing your website or improving search engine visibility',
    'Help with flyers, signs, or other physical marketing',
    'Help planning or improving your marketing strategy',
  ];

  techSupportOptions = [
    'Help with IT setup, troubleshooting, and online security',
    'Help setting up physical tech like servers, internet, etc.',
  ];

  businessInsuranceOptions = [
    'General business insurance',
    'Insurance for disasters (flood, fire, etc.)',
    'Property or building insurance',
    'Protection from lawsuits',
    'Insurance for key team members',
    'Coverage for directors or officers',
  ];

  newTechnologyOptions = ['Help using new digital tools in your business'];

  private businessBankingMap: Record<string, string> = {
    'Open or manage business accounts (checking, savings, investments)':
      'business_accounts',
    'Help with payroll and HR': 'payroll_and_hr',
    'Get a line of credit': 'lines_of_credit',
    "Help managing your company's cash flow": 'manage_cash_flow',
    'Advice on financial strategy': 'financial_strategy',
  };

  private marketingHelpMap: Record<string, string> = {
    'Help designing your website or improving search engine visibility':
      'website_design',
    'Help with flyers, signs, or other physical marketing':
      'physical_marketing',
    'Help planning or improving your marketing strategy': 'marketing_strategy',
  };

  private techSupportMap: Record<string, string> = {
    'Help with IT setup, troubleshooting, and online security': 'it_support',
    'Help setting up physical tech like servers, internet, etc.':
      'physical_infrastructure',
  };

  private businessInsuranceMap: Record<string, string> = {
    'General business insurance': 'general_insurance',
    'Insurance for disasters (flood, fire, etc.)': 'disaster_insurance',
    'Property or building insurance': 'building_insurance',
    'Protection from lawsuits': 'lawsuit_protection',
    'Insurance for key team members': 'key_team_insurance',
    'Coverage for directors or officers': 'directors_coverage',
  };

  private newTechnologyMap: Record<string, string> = {
    'Help using new digital tools in your business': 'new_digital_tools',
  };

  constructor(private fb: FormBuilder) {
    this.operationalToolsForm = this.fb.group({
      businessBanking: this.fb.group(
        this.businessBankingOptions.reduce((acc, option) => {
          acc[this.sanitizeKey(option)] = [false];
          return acc;
        }, {} as any),
      ),
      marketingHelp: this.fb.group(
        this.marketingHelpOptions.reduce((acc, option) => {
          acc[this.sanitizeKey(option)] = [false];
          return acc;
        }, {} as any),
      ),
      techSupport: this.fb.group(
        this.techSupportOptions.reduce((acc, option) => {
          acc[this.sanitizeKey(option)] = [false];
          return acc;
        }, {} as any),
      ),
      businessInsurance: this.fb.group(
        this.businessInsuranceOptions.reduce((acc, option) => {
          acc[this.sanitizeKey(option)] = [false];
          return acc;
        }, {} as any),
      ),
      newTechnology: this.fb.group(
        this.newTechnologyOptions.reduce((acc, option) => {
          acc[this.sanitizeKey(option)] = [false];
          return acc;
        }, {} as any),
      ),
    });
  }

  ngOnInit(): void {
    if (this.initialValue) {
      const patchData = { ...this.initialValue };

      // If selectedTools arrays exist, convert them back to checkbox objects
      if (patchData.selectedTools) {
        // Convert businessBanking array to object
        if (Array.isArray(patchData.selectedTools.businessBanking)) {
          const businessBankingObj: Record<string, boolean> = {};
          this.businessBankingOptions.forEach((option) => {
            const key = this.sanitizeKey(option);
            const backendValue = this.businessBankingMap[option];
            businessBankingObj[key] =
              patchData.selectedTools.businessBanking.includes(backendValue);
          });
          patchData.businessBanking = businessBankingObj;
        }

        // Convert marketingHelp array to object
        if (Array.isArray(patchData.selectedTools.marketingHelp)) {
          const marketingHelpObj: Record<string, boolean> = {};
          this.marketingHelpOptions.forEach((option) => {
            const key = this.sanitizeKey(option);
            const backendValue = this.marketingHelpMap[option];
            marketingHelpObj[key] =
              patchData.selectedTools.marketingHelp.includes(backendValue);
          });
          patchData.marketingHelp = marketingHelpObj;
        }

        // Convert techSupport array to object
        if (Array.isArray(patchData.selectedTools.techSupport)) {
          const techSupportObj: Record<string, boolean> = {};
          this.techSupportOptions.forEach((option) => {
            const key = this.sanitizeKey(option);
            const backendValue = this.techSupportMap[option];
            techSupportObj[key] =
              patchData.selectedTools.techSupport.includes(backendValue);
          });
          patchData.techSupport = techSupportObj;
        }

        // Convert businessInsurance array to object
        if (Array.isArray(patchData.selectedTools.businessInsurance)) {
          const businessInsuranceObj: Record<string, boolean> = {};
          this.businessInsuranceOptions.forEach((option) => {
            const key = this.sanitizeKey(option);
            const backendValue = this.businessInsuranceMap[option];
            businessInsuranceObj[key] =
              patchData.selectedTools.businessInsurance.includes(backendValue);
          });
          patchData.businessInsurance = businessInsuranceObj;
        }

        // Convert newTechnology array to object
        if (Array.isArray(patchData.selectedTools.newTechnology)) {
          const newTechnologyObj: Record<string, boolean> = {};
          this.newTechnologyOptions.forEach((option) => {
            const key = this.sanitizeKey(option);
            const backendValue = this.newTechnologyMap[option];
            newTechnologyObj[key] =
              patchData.selectedTools.newTechnology.includes(backendValue);
          });
          patchData.newTechnology = newTechnologyObj;
        }
      }

      this.operationalToolsForm.patchValue(patchData);
    }
  }

  sanitizeKey(option: string): string {
    return option.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  readonly hasErrors = false;

  public onSubmitForm() {
    const formValue = this.operationalToolsForm.value;
    const selectedTools = {
      businessBanking: this.extractSelected(
        formValue.businessBanking,
        this.businessBankingOptions,
        this.businessBankingMap,
      ),
      marketingHelp: this.extractSelected(
        formValue.marketingHelp,
        this.marketingHelpOptions,
        this.marketingHelpMap,
      ),
      techSupport: this.extractSelected(
        formValue.techSupport,
        this.techSupportOptions,
        this.techSupportMap,
      ),
      businessInsurance: this.extractSelected(
        formValue.businessInsurance,
        this.businessInsuranceOptions,
        this.businessInsuranceMap,
      ),
      newTechnology: this.extractSelected(
        formValue.newTechnology,
        this.newTechnologyOptions,
        this.newTechnologyMap,
      ),
    };

    this.formSubmit.emit({
      ...formValue,
      selectedTools,
    });
  }

  private extractSelected(
    group: Record<string, boolean>,
    options: string[],
    labelMap: Record<string, string>,
  ): string[] {
    if (!group) return [];

    return Object.entries(group)
      .filter(([, selected]) => selected)
      .map(([key]) => {
        const displayLabel = options.find(
          (option) => this.sanitizeKey(option) === key,
        );
        return displayLabel ? labelMap[displayLabel] : key;
      });
  }
}
